/**
 * Förhandlingsöverenskommelse om årlig hyreshöjning — two-part and three-part.
 *
 * Unlike the förhandlingsordning, every point here is numbered sequentially
 * and nothing cites a point by number, so the composition uses automatic
 * numbering: adding the fastighetsägarförening to the three-part variant
 * renumbers Hus from 3 to 4 and Överenskommelse from 4 to 5 by itself.
 */
import { COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { signingPartiesFor } from '../common/index.js';
import { partyPointLayout } from './forhandlingsordning.js';
const LESSOR = { id: 'lessor', label: 'Hyresvärdspart', role: 'lessor', ordinal: 0, required: true, signs: true };
const TENANT_ORG = {
    id: 'tenant_org',
    label: 'Hyresgästorganisation',
    role: 'lessee',
    ordinal: 0,
    required: true,
    signs: true,
    kind: 'organisation',
    help: 'Organisationen som företräder hyresgästerna i förhandlingsordningen.',
};
const PROPERTY_ASSOC = {
    id: 'property_assoc',
    label: 'Fastighetsägarförening',
    role: 'other',
    ordinal: 0,
    required: true,
    signs: true,
    kind: 'organisation',
    help: 'Den fastighetsägarförening som företräder hyresvärdsparten i trepartsförhandlingen.',
};
const PREAMBLE = 'Parterna i den förhandlingsordning som gäller för huset, har efter förhandlingar om den årliga revideringen av hyran för Huset kommit överens om följande.';
const PART_LESSOR = { instanceId: 'part-lessor', blockRef: 'forhandling.part-hyresvardspart@v1', ref: 'fok-part-lessor', title: 'Hyresvärdspart' };
const PART_ASSOC = {
    instanceId: 'part-property_assoc',
    blockRef: 'forhandling.part-fastighetsagarforening@v1',
    ref: 'fok-part-property_assoc',
    title: 'Fastighetsägarförening',
};
const PART_TENANT_ORG = {
    instanceId: 'part-tenant_org',
    blockRef: 'forhandling.part-hyresgastorganisation@v1',
    ref: 'fok-part-tenant_org',
    title: 'Hyresgästorganisation',
};
const TWO_PART = {
    documentKey: 'forhandlingsoverenskommelse_2part',
    documentVersion: 'v1',
    title: 'Förhandlingsöverenskommelse om årlig hyreshöjning – tvåparts · Version 2026',
    shortTitle: 'Förhandlingsöverenskommelse — tvåparts',
    formNumber: 'Formulär R-05',
    category: 'Forhandlingsoverenskommelse-2part',
    partySlots: [LESSOR, TENANT_ORG],
    partyPoints: [PART_LESSOR, PART_TENANT_ORG],
};
const THREE_PART = {
    documentKey: 'forhandlingsoverenskommelse_3part',
    documentVersion: 'v1',
    title: 'Förhandlingsöverenskommelse om årlig hyreshöjning – treparts · Version 2026',
    shortTitle: 'Förhandlingsöverenskommelse — treparts',
    formNumber: 'Formulär R-05-3',
    category: 'Forhandlingsoverenskommelse-3part',
    partySlots: [LESSOR, PROPERTY_ASSOC, TENANT_ORG],
    partyPoints: [PART_LESSOR, PART_ASSOC, PART_TENANT_ORG],
};
function build(variant, values = {}) {
    const partyPoints = variant.partyPoints.map((point, index, all) => ({
        instanceId: point.instanceId,
        blockRef: point.blockRef,
        input: { ref: point.ref, title: point.title, variant: 'clause', ...partyPointLayout(index, all.length) },
    }));
    return {
        schema: COMPOSITION_SCHEMA,
        version: COMPOSITION_VERSION,
        documentKey: variant.documentKey,
        documentVersion: variant.documentVersion,
        title: variant.title,
        locale: 'sv-SE',
        meta: {
            category: variant.category,
            numberStyle: 'point',
            shortTitle: variant.shortTitle,
            formNumber: variant.formNumber,
            statute: 'hyresförhandlingslagen (1978:304)',
            signingMode: 'digital',
            partySlots: variant.partySlots,
            propertySlots: [
                {
                    id: 'fastigheter',
                    label: 'Fastigheter (Huset)',
                    multi: true,
                    required: true,
                    help: 'Lägg till alla fastigheter som omfattas av denna överenskommelse.',
                },
            ],
            preamble: PREAMBLE,
        },
        values,
        blocks: [
            {
                instanceId: 'letterhead',
                blockRef: 'common.letterhead@v1',
                slot: 'letterhead',
                input: { title: variant.title, form_number: variant.formNumber },
            },
            {
                instanceId: 'footer',
                blockRef: 'common.page-footer@v1',
                slot: 'footer',
                input: { form_number: variant.formNumber },
            },
            { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE } },
            ...partyPoints,
            { instanceId: 'hus', blockRef: 'fok.hus@v1', input: {} },
            { instanceId: 'overenskommelse', blockRef: 'fok.overenskommelse@v1', input: {} },
            { instanceId: 'kopplade-avtal', blockRef: 'fok.kopplade-avtal@v1', input: {} },
            {
                instanceId: 'underskrifter',
                blockRef: 'forhandling.signing@v1',
                // Signing panels follow the party slots, in the legacy SigningBlock order.
                input: { statement: true, parties: signingPartiesFor(variant.partySlots) },
            },
        ],
    };
}
export const forhandlingsoverenskommelse2PartV1 = (values = {}) => build(TWO_PART, values);
export const forhandlingsoverenskommelse3PartV1 = (values = {}) => build(THREE_PART, values);
//# sourceMappingURL=forhandlingsoverenskommelse.js.map